<template>
  <div>
    <div
      class="loan-purpose-picker"
      role="radiogroup"
      :aria-labelledby="ariaLabelledby"
      :style="{borderBottomWidth: selectedPurpose && selectedPurpose.warn ? 0 : '1px'}"
    >
      <div v-for="(purposes, i) in __purposes" :key="i">
        <div v-for="(purpose, j) in purposes" :key="j" :class="{selected: purpose.description === value}"
             class="loan-purpose" @click="proxyValue = purpose.description" tabindex="0"
             ref="loanPurposesRef"
             role="radio"
             :aria-checked="purpose.description === value"
             @keydown.space="onProbableClick"
             @keydown.enter="onProbableClick"
        >
          <div v-if="purpose.description === value">
            <CheckCircleIcon :size="18"/>
          </div>
          <div v-else>
            <CircleOutlineIcon :size="18"/>
          </div>

          <span v-text="purpose.description"/>
        </div>
      </div>
    </div>

    <SmoothReflow :options="{property: ['height']}">
      <div v-if="selectedPurpose && selectedPurpose.warn" class="lp-tip">
        <div class="lp-tip__icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"/>
          </svg>
        </div>
        <p class="lp-tip__text" v-text="selectedPurpose.warn"/>
      </div>
    </SmoothReflow>
  </div>
</template>

<script setup>
import {LoanPurposes} from '@modules/guest/new-applicant/constants.js';
import chunk from 'lodash/chunk';
import SmoothReflow from '@modules/shared/transitions/SmoothReflow.vue';
import CheckCircleIcon from '@components/common/icons/CheckCircleIcon.vue';
import CircleOutlineIcon from '@components/common/icons/CircleOutlineIcon.vue';
import {computed, toRefs, unref, ref} from 'vue';

const emit = defineEmits(['input']);
const props = defineProps({
  value: {
    type: [String, null],
    required: false,
    default: null,
  },
  // Id of an external label element (e.g. the "Loan Purpose" section heading)
  // describing this radio group — wired to the container's aria-labelledby
  // rather than relying on attrs fallthrough, since this component's actual
  // template root wraps both the radiogroup and the warning-tip callout below it.
  ariaLabelledby: {
    type: String,
    required: false,
    default: null,
  },
});

const { value, ariaLabelledby } = toRefs(props);
const loanPurposesRef = ref();
const __purposes = chunk(LoanPurposes, Math.floor(LoanPurposes.length / 2));
const selectedPurpose = computed(() => LoanPurposes.filter(p => p.description === unref(value))[0] || null);

const proxyValue = computed({
  get: () => unref(value),
  set: (val) => {
    emit('input', val);
  },
});

const onProbableClick = (event) => {
  try {
    const target = [...(unref(loanPurposesRef) ?? [])].find(e => e === document.activeElement);

    if (target instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      target.click();
    }
  } catch {}
};
</script>

<style lang="scss" scoped>
$amber:    #FFB000;
$ink:      #15171C;
$ink-soft: #5B606B;
$line:     #E7E4DA;
$surface:  #FBFAF6;

.loan-purpose-picker {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.3rem;
  padding: 0.5rem;
  background: $surface;
  border: 1.5px solid $line;
  border-radius: 12px;
  font-size: 0.875rem;

  > div {
    min-width: 12rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .loan-purpose {
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
    cursor: pointer;
    transition: background 0.12s, outline 0.12s, color 0.12s;
    border-radius: 8px;
    padding: 0.5rem 0.6rem;
    color: $ink-soft;
    outline: 1.5px solid transparent;

    &:hover, &:focus {
      background: rgba(255, 176, 0, 0.08);
      outline: 1.5px solid rgba(255, 176, 0, 0.4);
      color: $ink;
    }

    > *:first-child {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      flex-shrink: 0;
      color: $line;
    }

    &:not(.selected) > *:first-child {
      background: #fff;
    }

    &.selected {
      color: $ink;
      font-weight: 600;
      background: rgba(255, 176, 0, 0.12);
      outline: 1.5px solid $amber;

      > *:first-child {
        color: $amber;
        background: transparent;
      }
    }
  }
}

// ── Tip / warn callout ────────────────────────────────────────────────────────
.lp-tip {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 10px;
  padding: 14px 16px;
  background: rgba(255, 176, 0, 0.07);
  border: 1.5px solid rgba(255, 176, 0, 0.28);
  border-radius: 12px;

  &__icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: rgba(255, 176, 0, 0.16);
    color: #A86E00;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__text {
    margin: 0;
    font-size: 13.5px;
    font-weight: 500;
    color: $ink-soft;
    line-height: 1.6;
    padding-top: 6px; /* vertically centres single-line text with the icon */
  }
}
</style>
